import Loader from '@/components/loader';
import useThread from '@/hooks/use-thread.hook';
import useUserSettings from '@/hooks/use-user-settings.hook';
import { Box, ClickAwayListener, Collapse, Fade, Stack, Theme } from '@mui/material';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import AgentOpt from './agent-opt';
import Icon from '@/components/icon';
import TextInput, { TextInputInterface } from '@/components/form/text-input';
import Text from '@/components/text';
import ResizeScrollbars from '@/components/resize-scrollbars';
import { useHotkeys } from 'react-hotkeys-hook';

interface Props {
  onChangeOpen?: (open: boolean) => void;
}

const AGENT_OPT_HEIGHT = 60;
const COLLAPSE_TIMEOUT = 200;

const useStyles = createUseStyles((theme: Theme) => ({
  focusTarget: {
    outline: 'none',
  },
  agentSelector: {
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  agentList: {
    maxHeight: AGENT_OPT_HEIGHT * 5,
  },
  agentSelected: {
    height: AGENT_OPT_HEIGHT,
    padding: '0px 8px 0px 0px',
  },
  agentSelectInput: {
    height: AGENT_OPT_HEIGHT,
    padding: 8,
    '& fieldset': {
      border: `1px solid ${theme.palette.divider} !important`,
    },
  },
  agentOptList: {
    '& .agent-opt': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '& .agent-opt:last-child': {
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
  },
}));

const AgentSelector: FC<Props> = ({ onChangeOpen }) => {
  const styles = useStyles();
  const { settings, setAgentId } = useThread();
  const { agents, isLoading: showLoader } = useUserSettings();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const inputRef = useRef<TextInputInterface>(null);

  const { agentId } = settings;

  const handleClickAgentOpt = (agentId: string) => {
    setAgentId(agentId);
    setOpen(false);
  };

  const handleToggleOpen = () => {
    setOpen(!open);
    if (!open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleKeywordChange = (val: string) => {
    setKeyword(val);
  };

  const handleSelectNextAgent = () => {
    const selectedIndex = agents.findIndex(({ id }) => id === agentId);
    let updatedIndex = selectedIndex + 1;

    if (updatedIndex >= agents.length) {
      updatedIndex = 0;
    }

    setAgentId(agents[updatedIndex].id);
  };

  const handleSelectPrevAgent = () => {
    const selectedIndex = agents.findIndex(({ id }) => id === agentId);
    let updatedIndex = selectedIndex - 1;

    if (updatedIndex < 0) {
      updatedIndex = agents.length - 1;
    }

    setAgentId(agents[updatedIndex].id);
  };

  const handleSelectAgent = () => {
    setOpen(!open);
  };

  const handleFocus = () => {
    if (!open) {
      handleToggleOpen();
    }
  };

  const fOrgAgents = useMemo(() => {
    const safeKeyword = String(keyword).trim().toLowerCase();
    return agents.filter(({ portal }) => {
      const { description, greeting, badge } = portal;
      return [description, greeting, badge?.title]
        .filter((v) => v)
        .map((v) => String(v).toLowerCase())
        .some((v) => v.includes(safeKeyword));
    });
  }, [agents, keyword]);

  const hasMultipleAgents = agents.length > 1;

  useHotkeys('esc', handleClose, { enableOnFormTags: ['input'] });
  useHotkeys('down', handleSelectNextAgent, { enableOnFormTags: ['input'], preventDefault: true });
  useHotkeys('up', handleSelectPrevAgent, { enableOnFormTags: ['input'], preventDefault: true });
  useHotkeys('enter', handleSelectAgent, { enableOnFormTags: ['input'], preventDefault: true });

  useEffect(() => {
    onChangeOpen?.(open);
    if (!open) {
      setKeyword('');
    }
  }, [open, onChangeOpen]);

  if (showLoader) {
    return <Loader />;
  }

  if (!hasMultipleAgents) {
    return null;
  }

  const selectedAgent = agents.find(({ id }) => id === agentId) || agents[0];
  const noSearchMatch = !!keyword && !fOrgAgents.length;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box width="100%" className={styles.focusTarget} onFocus={handleFocus}>
        <Stack
          className={styles.agentSelector}
          tabIndex={2}
          role="button"
          onClick={open ? undefined : handleToggleOpen}
        >
          <Stack direction="row" width="100%" className={styles.agentSelected} alignItems="center">
            <Box flexGrow={1}>
              {open && (
                <Box className={styles.agentSelectInput}>
                  <TextInput
                    name="agent-select-keyword"
                    value={keyword}
                    onChange={handleKeywordChange}
                    placeholder="Search by keyword"
                    size="small"
                    ref={inputRef}
                    clearable
                    autoComplete="off"
                  />
                </Box>
              )}
              {!open && <AgentOpt agent={selectedAgent} height={AGENT_OPT_HEIGHT} />}
            </Box>

            <Icon
              name={open ? 'expand-less' : 'expand-more'}
              onClick={handleToggleOpen}
              size="small"
            />
          </Stack>
          <Collapse in={open} timeout={COLLAPSE_TIMEOUT} unmountOnExit className={styles.agentList}>
            <ResizeScrollbars autoHeight autoHeightMax={AGENT_OPT_HEIGHT * 4}>
              {noSearchMatch && (
                <Box p={1}>
                  <Text italic>No assistants found</Text>
                </Box>
              )}
              <Box className={styles.agentOptList}>
                {fOrgAgents.map((agent) => {
                  const selected = agent.id === agentId;
                  return (
                    <AgentOpt
                      agent={agent}
                      key={agent.id}
                      onClick={handleClickAgentOpt}
                      selected={selected}
                      height={AGENT_OPT_HEIGHT}
                    />
                  );
                })}
              </Box>
            </ResizeScrollbars>
          </Collapse>
        </Stack>

        <Box mt={4}>
          <Fade in={!open} mountOnEnter unmountOnExit timeout={COLLAPSE_TIMEOUT + 10}>
            <Stack width="100%" alignItems="center">
              <Text>{selectedAgent?.portal.greeting}</Text>
            </Stack>
          </Fade>
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export default AgentSelector;
